import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, RotateCcw } from "lucide-react";

const API = `/api`;

const profiles = [
  {
    id: "porteur",
    label: "Porteur de projet",
    wishes: [
      "Constituer ma structure",
      "Organiser les relations entre associés",
      "Évaluer la faisabilité de mon projet",
    ],
  },
  {
    id: "entreprise",
    label: "Entreprise",
    wishes: [
      "Sécuriser, adapter ou mettre fin à mes contrats",
      "Financer mon développement ou lever des fonds",
      "Me mettre en conformité (AG, formalités, RGPD)",
      "Mon cocontractant ne tient pas ses engagements",
      "Engager ou me séparer de personnel",
    ],
  },
  {
    id: "actionnaire",
    label: "Actionnaire",
    wishes: [
      "Exercer mes droits d'actionnaire ou d'associé",
      "Gérer un conflit entre associés",
      "Céder ou acquérir des actions",
    ],
  },
  {
    id: "dirigeant",
    label: "Dirigeant",
    wishes: [
      "Optimiser ma rémunération et mes incentives",
      "Structurer mon contrat de management ou ma société de management",
      "Protéger ma responsabilité personnelle",
      "Bien exercer mon mandat (formalités, AG, actionnaires)",
    ],
  },
  {
    id: "cedant",
    label: "Cédant",
    wishes: [
      "Vendre ou transmettre mon activité",
      "Me séparer de mes associés ou résoudre un litige",
    ],
  },
  {
    id: "repreneur",
    label: "Repreneur ou investisseur",
    wishes: [
      "Reprendre une entreprise ou une activité",
      "Acquérir une participation (majoritaire ou minoritaire)",
      "Prêter à une entreprise",
      "Récupérer les fonds que j'ai prêtés ou investis",
    ],
  },
];

const CATCH_ALL = "Une autre question ?";

const chip = (active) =>
  `px-5 py-3 text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${
    active
      ? "bg-gold text-ink border-gold font-medium"
      : "border-white/15 text-[#A0A5B1] hover:border-gold/60 hover:text-gold"
  }`;

export default function Wizard() {
  const [profile, setProfile] = useState(null);
  const [wish, setWish] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSugg, setLoadingSugg] = useState(false);

  useEffect(() => {
    if (!wish || !profile || wish === CATCH_ALL) {
      setSuggestions([]);
      return;
    }
    setLoadingSugg(true);
    setSuggestions([]);
    fetch(`${API}/wizard/suggestions?profile=${encodeURIComponent(profile.id)}&wish=${encodeURIComponent(wish)}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        setSuggestions(d);
        setLoadingSugg(false);
      })
      .catch(() => setLoadingSugg(false));
  }, [wish, profile]);

  const reset = () => {
    setProfile(null);
    setWish(null);
  };

  const goContact = () => {
    const target = document.querySelector("#contact");
    if (target && window.__lenis) window.__lenis.scrollTo(target, { offset: -72, duration: 1.4 });
  };

  return (
    <section className="relative py-24 sm:py-32 bg-ink" data-testid="wizard-section">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="label-accent" data-testid="wizard-overline">
            Découvrez ce que nous pouvons faire pour vous
          </span>
          <h2 className="mt-6 font-serif font-light tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Dites-nous <em className="italic text-gold-gradient not-italic">qui vous êtes</em>,
            <br className="hidden sm:block" /> nous vous disons comment.
          </h2>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <p className="font-mono text-xs tracking-[0.25em] text-gold/70 uppercase mb-6">01 — Je suis</p>
            <div className="flex flex-wrap gap-3" data-testid="wizard-profiles">
              {profiles.map((p) => (
                <button
                  key={p.id}
                  onClick={() => { setProfile(p); setWish(null); }}
                  className={chip(profile?.id === p.id)}
                  data-testid={`wizard-profile-${p.id}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </motion.div>

          <div className="lg:col-span-7">
            <p className="font-mono text-xs tracking-[0.25em] text-gold/70 uppercase mb-6">02 — Je souhaite</p>
            <AnimatePresence mode="wait">
              {!profile ? (
                <motion.p
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-sm text-[#626773] italic font-serif text-lg"
                  data-testid="wizard-placeholder"
                >
                  Sélectionnez d'abord votre profil…
                </motion.p>
              ) : (
                <motion.div
                  key={profile.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="flex flex-wrap gap-3" data-testid="wizard-wishes">
                    {[...profile.wishes, CATCH_ALL].map((w) => (
                      <button
                        key={w}
                        onClick={() => setWish(w)}
                        className={chip(wish === w)}
                        data-testid={`wizard-wish-${w.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      >
                        {w}
                      </button>
                    ))}
                  </div>

                  <AnimatePresence>
                    {wish && (
                      <motion.div
                        key="result"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-10 bg-ink-card border border-gold/25 p-8 sm:p-10"
                        data-testid="wizard-result"
                      >
                        <p className="label-accent">e².law peut vous aider à</p>
                        <p className="mt-4 font-serif text-2xl sm:text-3xl leading-snug">
                          «&nbsp;{wish}&nbsp;»
                        </p>
                        {wish === CATCH_ALL ? (
                          <div className="mt-4" data-testid="wizard-catchall">
                            <p className="text-sm text-[#A0A5B1] leading-relaxed max-w-lg">
                              Dites-nous quel sujet vous intéresse. Notre portail complet compte
                              plus de 700 fiches — et un échange nous permet de vous orienter
                              directement.
                            </p>
                            <Link
                              to="/ressources"
                              data-testid="wizard-catchall-ressources"
                              className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold hover:gap-3 transition-all duration-300"
                            >
                              Parcourir les fiches <ArrowRight className="w-4 h-4" />
                            </Link>
                          </div>
                        ) : (
                        <p className="mt-4 text-sm text-[#A0A5B1] leading-relaxed max-w-lg">
                          Structuration, rédaction, négociation — et, si nécessaire, défense de vos
                          intérêts devant les tribunaux de l'entreprise. En français, en anglais
                          ou en néerlandais.
                        </p>
                        )}
                        {loadingSugg && (
                          <p className="mt-6 text-xs text-[#626773] font-mono tracking-widest uppercase animate-pulse" data-testid="wizard-suggestions-loading">
                            Sélection des fiches pertinentes…
                          </p>
                        )}
                        {!loadingSugg && suggestions.length === 0 && (
                          <p className="mt-6 text-sm text-[#626773] italic font-serif" data-testid="wizard-no-suggestion">
                            Ce besoin précis n'a pas encore de fiche dédiée dans notre base —
                            un échange nous permettra d'y répondre directement.
                          </p>
                        )}
                        {!loadingSugg && suggestions.length > 0 && (
                          <div className="mt-6" data-testid="wizard-suggestions">
                            <p className="label-accent mb-3">Fiches liées dans notre base</p>
                            <div className="flex flex-col gap-2">
                              {suggestions.map((s) => (
                                <Link
                                  key={`${s.type}-${s.slug}`}
                                  to={`/ressources/${s.type}/${s.slug}`}
                                  data-testid={`wizard-fiche-${s.slug}`}
                                  className="group flex items-center justify-between gap-4 border border-white/10 hover:border-gold/50 px-4 py-3 transition-colors"
                                >
                                  <span className="text-sm text-[#A0A5B1] group-hover:text-[#FAF8F5] transition-colors">
                                    {s.title}
                                  </span>
                                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold/70 shrink-0">
                                    {s.type_label}
                                  </span>
                                </Link>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="mt-8 flex flex-wrap gap-4">
                          <button
                            onClick={goContact}
                            data-testid="wizard-cta-contact"
                            className="inline-flex items-center gap-2 bg-gold text-ink text-xs uppercase tracking-[0.25em] font-medium px-6 py-3.5 hover:bg-gold-light transition-colors duration-300"
                          >
                            Discutons-en <ArrowRight className="w-4 h-4" />
                          </button>
                          <button
                            onClick={reset}
                            data-testid="wizard-reset"
                            className="inline-flex items-center gap-2 border border-white/15 text-[#A0A5B1] text-xs uppercase tracking-[0.25em] px-6 py-3.5 hover:border-gold/60 hover:text-gold transition-colors duration-300"
                          >
                            <RotateCcw className="w-3.5 h-3.5" /> Recommencer
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
